// Scale Bridge — sign in.
//
// Split screen: what this software does on the left, the form on the right.
//
// ─── The flow ──────────────────────────────────────────────────────────
//
//     computer already activated?
//         yes  ->  username + password ───────────────────────────────►  dashboard
//         no   ->  activation dialog (licence pair only)
//                      └─ activates with the credentials already typed ►  dashboard
//                         and the pair is remembered for next time
//
// The username and password are typed ONCE, on this form. If the machine turns out
// not to be activated, they are carried into the dialog rather than asked for again —
// the dialog asks only for the two values the operator is reading off a certificate.
//
// The WeighBridge ID and License Key identify the SHOP and never change between
// shifts. Asking for them every morning means the certificate ends up taped to the
// monitor, which is worse for security than storing them once on a machine already
// trusted to display the shop's weighments.
//
// ─── A stored licence that stops working ───────────────────────────────
// The EXPECTED failure, not an exotic one: a renewal issues a new key and the old one
// stops resolving. With a stored licence, `invalid_credentials` is genuinely
// ambiguous — it may be the password or it may be the licence, and the server
// deliberately cannot tell us which (one answer for every wrong credential, so the
// endpoint is not an oracle). So the message names BOTH possibilities and offers
// re-activation alongside it, rather than sending somebody to reset a password that
// was never wrong.
import { useState } from 'react';
import { Form, Input, Button, Typography, Alert } from 'antd';
import {
    ApiOutlined,
    LockOutlined,
    MobileOutlined,
    SafetyCertificateOutlined,
    ThunderboltOutlined,
    UserOutlined,
} from '@ant-design/icons';

import {
    login,
    provision,
    LoginError,
    LicenseRejectedError,
    ProvisioningUnreachableError,
    AccountRequiredError,
} from '../api/client';
import { useAuthStore } from '../state/authStore';
import { BRAND } from '../theme';
import ActivationDialog, { type ActivationValues } from '../components/ActivationDialog';
import { loadActivation, saveActivation } from '../lib/activation';

const { Title, Text } = Typography;

export default function Login() {
    const signIn = useAuthStore((s) => s.signIn);

    const [activation, setActivation] = useState(() => loadActivation());

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);
    const [reactivating, setReactivating] = useState(false);
    // Carried into the dialog so the operator is not asked twice for what they typed.
    const [pending, setPending] = useState<{ username: string; password: string }>({
        username: '',
        password: '',
    });
    // ─── Why the form needs an instance ─────────────────────────────────
    // onActivate() signs in with `pending`, and `pending` used to be set in exactly one
    // place: onSubmit, the sign-in form's own handler. Every OTHER way into the dialog -
    // "Change", and the "Update licence" action on the licence alert - opened it
    // directly, so pending was still {username:'', password:''}.
    //
    // The result was the bug this fixes. You typed a correct WeighBridge ID and License
    // Key, and the dialog answered "Those sign-in details were not correct" - because
    // the login it ran behind the dialog carried an empty username and password. The
    // message named the one thing that was NOT wrong, so the natural response was to
    // re-check the licence certificate, which never helped.
    const [form] = Form.useForm<{ username: string; password: string }>();

    const baseUrl = activation?.baseUrl || window.location.origin;


    /**
     * Turn a login failure into something the operator can act on.
     *
     * Only `invalid_credentials` is ambiguous, and only when a licence is stored.
     * Everything else — disabled, inactive, expired — is shown verbatim, because the
     * credentials were right and the reason is actionable.
     */
    function describe(err: unknown, hadStoredLicence: boolean): string {
        if (err instanceof AccountRequiredError) return err.message;
        if (err instanceof LoginError && err.code !== 'invalid_credentials') return err.message;
        if (!(err instanceof LoginError)) {
            return err instanceof Error ? err.message : 'Could not reach the receiver.';
        }
        return hadStoredLicence
            ? "Wrong username or password — or this computer's License Key is out of date."
            : 'Those sign-in details were not correct.';
    }

    function adopt(token: string, username: string, accountName: string, role: string) {
        signIn({ baseUrl: activation?.baseUrl ?? '', token, username, accountName, role });
    }

    /**
     * Open the licence dialog, carrying whatever is already typed on the form.
     *
     * Changing the pair is not a read-only act: it provisions the new identity and then
     * SIGNS IN with it, because a licence that cannot be signed in to has not been
     * proved and must not be stored. So the username and password are required here,
     * and asking for them up front is the difference between a control that works and
     * one that fails with a message about the wrong field.
     */
    async function openIdentityDialog(): Promise<void> {
        let values: { username: string; password: string };
        try {
            // Ant Design's own validation, so the empty fields get their normal inline
            // markers rather than a banner explaining them.
            values = await form.validateFields();
        } catch {
            setError(
                'Enter your username and password first. Changing the WeighBridge ID and '
                + 'License Key signs this computer in with them.',
            );
            return;
        }
        setError(null);
        setPending({ username: values.username, password: values.password });
        setDialogError(null);
        setReactivating(true);
        setDialogOpen(true);
    }

    // ─── The normal path: this computer is activated ────────────────────
    async function onSubmit(values: { username: string; password: string }) {
        setPending(values);
        const stored = loadActivation();

        if (!stored) {
            // Never activated. Open the dialog for the licence pair and nothing the
            // operator has already given.
            setError(null);
            setDialogError(null);
            setReactivating(false);
            setDialogOpen(true);
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const result = await login({
                baseUrl: stored.baseUrl || window.location.origin,
                username: values.username.trim(),
                password: values.password,
                weighbridgeId: stored.weighbridgeId,
                licenseKey: stored.licenseKey,
            });
            adopt(result.token, result.username, result.accountName, result.role);
        } catch (err) {
            // A licence problem is about the SHOP, and the fix is a new key rather than
            // a new password — so it opens the dialog instead of scolding the operator.
            if (err instanceof LoginError &&
                (err.code === 'license_expired' || err.code === 'account_inactive')) {
                setDialogError(err.message);
                setReactivating(true);
                setDialogOpen(true);
            } else {
                setError(describe(err, true));
            }
        } finally {
            setLoading(false);
        }
    }

    // ─── Activation: the licence pair plus the credentials already typed ─
    async function onActivate(values: ActivationValues) {
        setLoading(true);
        setDialogError(null);
        try {
            // ─── Provision FIRST, then sign in ──────────────────────────
            // This is the step that CREATES the account. A shop typing a valid
            // WeighBridge ID and License Key for the first time has no account yet,
            // so a login-only activation can never do anything but say "not
            // correct" - for credentials that are perfectly right.
            //
            // Idempotent, so activating a second computer arrives here and simply
            // gets the existing account back.
            try {
                await provision({
                    baseUrl,
                    weighbridgeId: values.weighbridge_id.trim(),
                    licenseKey: values.license_key.trim(),
                });
            } catch (err) {
                if (err instanceof LicenseRejectedError) {
                    // The pair was adjudicated and refused. Say so here rather than
                    // letting it surface as a wrong-password message.
                    setDialogError(err.message);
                    return;
                }
                if (!(err instanceof ProvisioningUnreachableError)) throw err;
                // Unreachable. NOT fatal - this shop may already be provisioned and
                // simply be activating another computer, in which case the sign-in
                // below succeeds on its own. Refusing here would make a transient
                // outage look like a bad licence.
            }

            const result = await login({
                baseUrl,
                username: pending.username.trim(),
                password: pending.password,
                weighbridgeId: values.weighbridge_id.trim(),
                licenseKey: values.license_key.trim(),
            });
            // Stored only after it has been proved to work. Storing first and failing
            // on the next screen is indistinguishable from a broken install.
            const next = {
                baseUrl: activation?.baseUrl ?? '',
                weighbridgeId: values.weighbridge_id.trim(),
                licenseKey: values.license_key.trim(),
                accountName: result.accountName,
            };
            saveActivation(next);
            setActivation(next);
            setDialogOpen(false);
            signIn({
                baseUrl: next.baseUrl,
                token: result.token,
                username: result.username,
                accountName: result.accountName,
                role: result.role,
            });
        } catch (err) {
            setDialogError(describe(err, false));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="sb-login">
            <div className="sb-login__brand">
                <div>
                    <div className="sb-logo">
                        <div className="sb-logo__mark">SB</div>
                        <div>
                            <div className="sb-logo__text">{BRAND.name}</div>
                            <div className="sb-logo__sub">{BRAND.tagline}</div>
                        </div>
                    </div>

                    <h1 className="sb-login__title">
                        Every weighment,
                        <br />
                        the moment it prints.
                    </h1>
                    <p className="sb-login__lede">
                        Labels captured straight off the machine, on your screen before the
                        operator has torn the ticket off — and on the customer&rsquo;s phone
                        before they reach the gate.
                    </p>

                    <ul className="sb-login__features">
                        <li>
                            <span className="sb-login__check">
                                <ThunderboltOutlined />
                            </span>
                            Live feed, under a second from print to screen
                        </li>
                        <li>
                            <span className="sb-login__check">
                                <MobileOutlined />
                            </span>
                            A bill the customer opens with no app and no login
                        </li>
                        <li>
                            <span className="sb-login__check">
                                <SafetyCertificateOutlined />
                            </span>
                            An unreadable weight is recorded as unknown, never as zero
                        </li>
                        <li>
                            <span className="sb-login__check">
                                <ApiOutlined />
                            </span>
                            Every device, every shift, in one place
                        </li>
                    </ul>
                </div>

                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                    © {new Date().getFullYear()} JBC · Confidential
                </Text>
            </div>

            <div className="sb-login__form-pane">
                <div className="sb-login__form">
                    <Title level={3} style={{ marginBottom: 4 }}>
                        Welcome back
                    </Title>
                    <Text type="secondary">
                        {activation?.accountName
                            ? `Sign in to ${activation.accountName}`
                            : 'Sign in to continue to the dashboard'}
                    </Text>

                    {error && (
                        <Alert
                            type="error"
                            showIcon
                            message={error}
                            style={{ marginTop: 20 }}
                            action={
                                activation ? (
                                    <Button
                                        size="small"
                                        type="link"
                                        onClick={() => void openIdentityDialog()}
                                    >
                                        Update licence
                                    </Button>
                                ) : undefined
                            }
                        />
                    )}

                    <Form
                        form={form}
                        layout="vertical"
                        requiredMark={false}
                        onFinish={onSubmit}
                        style={{ marginTop: 24 }}
                    >
                            <Form.Item
                                name="username"
                                label="Username"
                                rules={[{ required: true, message: 'Your username is required.' }]}
                            >
                                <Input
                                    prefix={<UserOutlined />}
                                    autoComplete="username"
                                    size="large"
                                    disabled={loading}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label="Password"
                                rules={[{ required: true, message: 'Your password is required.' }]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    autoComplete="current-password"
                                    size="large"
                                    disabled={loading}
                                />
                            </Form.Item>

                            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
                                Sign in
                            </Button>

                            <div className="sb-login__meta">
                                {activation ? (
                                    <>
                                        <span>
                                            <IdBadge /> {activation.weighbridgeId}
                                        </span>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => void openIdentityDialog()}
                                        >
                                            Change
                                        </Button>
                                    </>
                                ) : (
                                    <span style={{ fontSize: 12 }}>
                                        This computer will be activated on first sign-in.
                                    </span>
                                )}
                            </div>

                    </Form>
                </div>
            </div>

            <ActivationDialog
                open={dialogOpen}
                loading={loading}
                error={dialogError}
                reactivating={reactivating}
                currentWeighbridgeId={activation?.weighbridgeId ?? null}
                onCancel={() => setDialogOpen(false)}
                onSubmit={onActivate}
            />
        </div>
    );
}

/** The small identity glyph beside a stored WeighBridge ID. */
function IdBadge() {
    return <ApiOutlined style={{ color: BRAND.muted }} />;
}
